
import { useState } from 'react';

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: string;
  modified: string;
  path: string;
  extension?: string;
}

const FileManager = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'file' | 'folder'>('file');
  const [newItemName, setNewItemName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const files: FileItem[] = [
    { id: '1', name: 'src', type: 'folder', modified: '2024-01-15', path: '/src' },
    { id: '2', name: 'public', type: 'folder', modified: '2024-01-14', path: '/public' },
    { id: '3', name: 'package.json', type: 'file', size: '2.1 KB', modified: '2024-01-13', path: '/package.json', extension: 'json' },
    { id: '4', name: 'vite.config.ts', type: 'file', size: '0.9 KB', modified: '2024-01-12', path: '/vite.config.ts', extension: 'ts' },
    { id: '5', name: 'tailwind.config.ts', type: 'file', size: '1.2 KB', modified: '2024-01-11', path: '/tailwind.config.ts', extension: 'ts' },
    { id: '6', name: 'README.md', type: 'file', size: '3.4 KB', modified: '2024-01-10', path: '/README.md', extension: 'md' },
    { id: '7', name: 'tsconfig.json', type: 'file', size: '0.8 KB', modified: '2024-01-09', path: '/tsconfig.json', extension: 'json' },
    { id: '8', name: '.gitignore', type: 'file', size: '0.5 KB', modified: '2024-01-08', path: '/.gitignore', extension: 'txt' }
  ];

  const breadcrumbs = currentPath.split('/').filter(Boolean);

  const toggleSelection = (itemId: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    setSelectedItems(newSelection);
  };

  const selectAll = () => {
    if (selectedItems.size === files.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(files.map(f => f.id)));
    }
  };

  const getFileIcon = (file: FileItem) => {
    if (file.type === 'folder') return 'ri-folder-line';
    
    switch (file.extension) {
      case 'ts':
      case 'tsx': return 'ri-file-code-line';
      case 'json': return 'ri-file-text-line';
      case 'md': return 'ri-markdown-line';
      case 'css': return 'ri-css3-line';
      case 'js':
      case 'jsx': return 'ri-javascript-line';
      default: return 'ri-file-line';
    }
  };

  const getFileColor = (file: FileItem) => {
    if (file.type === 'folder') return 'text-blue-600';
    
    switch (file.extension) {
      case 'ts':
      case 'tsx': return 'text-blue-500';
      case 'json': return 'text-yellow-600';
      case 'md': return 'text-gray-600';
      case 'css': return 'text-purple-600';
      case 'js':
      case 'jsx': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  const handleCreateItem = () => {
    if (!newItemName.trim()) {
      alert('Please enter a name for the new ' + createType);
      return;
    }

    console.log(`Creating new ${createType}:`, newItemName, 'at', currentPath);
    alert(`${createType === 'file' ? 'File' : 'Folder'} "${newItemName}" created successfully!\n\nLocation: ${currentPath}\n\nNote: In production, this would create the actual ${createType}.`);
    setShowCreateModal(false);
    setNewItemName('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    console.log('Uploading file:', selectedFile.name, 'to', currentPath);
    alert(`File "${selectedFile.name}" uploaded successfully!\n\nSize: ${(selectedFile.size / 1024).toFixed(2)} KB\nLocation: ${currentPath}\n\nNote: In production, this would upload the actual file.`);
    setShowUploadModal(false);
    setSelectedFile(null);
  };

  const handleDelete = () => {
    if (selectedItems.size === 0) return;

    const itemNames = Array.from(selectedItems).map(id => {
      const file = files.find(f => f.id === id);
      return file?.name;
    }).join(', ');

    if (confirm(`Are you sure you want to delete ${selectedItems.size} item(s)?\n\n${itemNames}\n\nThis action cannot be undone.`)) {
      console.log('Deleting items:', Array.from(selectedItems));
      alert(`${selectedItems.size} item(s) deleted successfully!\n\nNote: In production, this would delete the actual files.`);
      setSelectedItems(new Set());
    }
  };

  const handleDownload = () => {
    if (selectedItems.size === 0) return;
    
    const itemNames = Array.from(selectedItems).map(id => {
      const file = files.find(f => f.id === id);
      return file?.name;
    }).join(', ');

    alert(`Downloading ${selectedItems.size} item(s):\n\n${itemNames}\n\nNote: In production, this would download the selected files.`);
  };

  const handleCopy = () => {
    if (selectedItems.size === 0) return;
    alert(`${selectedItems.size} item(s) copied to clipboard\n\nNote: In production, this would copy the files for pasting.`);
  };

  const handleCut = () => {
    if (selectedItems.size === 0) return;
    alert(`${selectedItems.size} item(s) cut to clipboard\n\nNote: In production, this would prepare the files for moving.`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">File Manager</h2>
        <p className="text-gray-600">Manage project files and directories</p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm">
              <button
                onClick={() => setCurrentPath('/')}
                className="text-red-600 hover:text-red-700 font-medium"
              >
                <i className="ri-home-line mr-1"></i>
                Root
              </button>
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center">
                  <i className="ri-arrow-right-s-line text-gray-400 mx-1"></i>
                  <button
                    onClick={() => setCurrentPath('/' + breadcrumbs.slice(0, index + 1).join('/'))}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    {crumb}
                  </button>
                </div>
              ))}
            </nav>
          </div>

          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-red-50 text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-list-unordered"></i>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-red-50 text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-grid-line"></i>
              </button>
            </div>

            {/* Actions */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
            >
              <i className="ri-upload-line mr-2"></i>
              Upload
            </button>

            <button
              onClick={() => {
                setCreateType('folder');
                setShowCreateModal(true);
              }}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
            >
              <i className="ri-folder-add-line mr-2"></i>
              New Folder
            </button>

            <button
              onClick={() => {
                setCreateType('file');
                setShowCreateModal(true);
              }}
              className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap"
            >
              <i className="ri-file-add-line mr-2"></i>
              New File
            </button>
          </div>
        </div>

        {/* Selection Actions */}
        {selectedItems.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownload}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 whitespace-nowrap"
              >
                <i className="ri-download-line mr-1"></i>
                Download
              </button>
              <button
                onClick={handleCopy}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 whitespace-nowrap"
              >
                <i className="ri-file-copy-line mr-1"></i>
                Copy
              </button>
              <button
                onClick={handleCut}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 whitespace-nowrap"
              >
                <i className="ri-scissors-line mr-1"></i>
                Cut
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* File List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedItems.size === files.length}
              onChange={selectAll}
              className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">Name</span>
          </div>
          <div className="flex items-center space-x-8 text-sm font-medium text-gray-900">
            <span>Size</span>
            <span>Modified</span>
            <span>Actions</span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {files.map((file) => (
            <div
              key={file.id}
              className={`px-6 py-4 hover:bg-gray-50 ${selectedItems.has(file.id) ? 'bg-red-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(file.id)}
                    onChange={() => toggleSelection(file.id)}
                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                  />
                  <div className="ml-3 flex items-center">
                    <i className={`${getFileIcon(file)} ${getFileColor(file)} text-lg mr-3`}></i>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{file.name}</div>
                      <div className="text-xs text-gray-500">{file.path}</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-8 text-sm text-gray-500">
                  <span className="w-16 text-right">{file.size || '-'}</span>
                  <span className="w-24 text-right">{file.modified}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => alert(`Viewing: ${file.name}\n\nNote: In production, this would open the file viewer.`)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="View"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button
                      onClick={() => alert(`Editing: ${file.name}\n\nNote: In production, this would open the file editor.`)}
                      className="p-1 text-gray-400 hover:text-green-600"
                      title="Edit"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${file.name}"?\n\nThis action cannot be undone.`)) {
                          alert(`"${file.name}" deleted successfully!`);
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                    <button
                      onClick={() => alert(`More options for: ${file.name}`)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="More"
                    >
                      <i className="ri-more-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Storage Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-folder-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">247</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-hard-drive-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="text-2xl font-bold text-gray-900">2.4 GB</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-cloud-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Available</p>
              <p className="text-2xl font-bold text-gray-900">7.6 GB</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Upload Files</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                <p className="text-lg font-medium text-gray-900 mb-2">Drop files here</p>
                <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Choose Files
                </label>
                {selectedFile && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-left">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="ri-file-line text-gray-600 mr-2"></i>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg whitespace-nowrap ${
                  selectedFile ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Create New {createType === 'file' ? 'File' : 'Folder'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewItemName('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {createType === 'file' ? 'File' : 'Folder'} Name
                  </label>
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder={createType === 'file' ? 'example.tsx' : 'new-folder'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={currentPath}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewItemName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateItem}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap"
              >
                Create {createType === 'file' ? 'File' : 'Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
